import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll, useSpring } from "framer-motion";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import MetaPixel from "@/components/MetaPixel";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import LandingPage from "@/pages/LandingPage";
import CheckoutPage from "@/pages/CheckoutPage";
import AdminOrders from "@/pages/AdminOrders";
import TrackOrderPage from "@/pages/TrackOrderPage";
import ConfirmOrderPage from "@/pages/ConfirmOrderPage";
import LegalPage from "@/pages/LegalPage";
import "@/App.css";

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({
      lerp: 0.12,
      smoothWheel: true,
      touchMultiplier: 1.8,
    });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.3 });
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed inset-x-0 top-0 z-[100] h-[3px] origin-left bg-gradient-to-r from-sage-500 via-gold to-sage-400"
      data-testid="scroll-progress"
    />
  );
}

function App() {
  return (
    <ErrorBoundary>
      <CartProvider>
        <MetaPixel />
        <ScrollProgress />
        <BrowserRouter>
          <ScrollToTop />
          <SmoothScroll />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/track" element={<TrackOrderPage />} />
            <Route path="/confirm" element={<ConfirmOrderPage />} />
            <Route path="/about" element={<LegalPage kind="about" />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/shipping" element={<LegalPage kind="shipping" />} />
            <Route path="/returns" element={<LegalPage kind="returns" />} />
            <Route path="*" element={<LandingPage />} />
          </Routes>
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </CartProvider>
    </ErrorBoundary>
  );
}

export default App;
